"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import { useSearchParams } from "next/navigation";
import Link from "next/link";

import { InformativaConsensi } from "@/components/contatti/InformativaConsensi";
import { MiniMessaggio, MiniProfili } from "@/components/contatti/DisegniContatti";
import { Icona, type NomeIcona } from "@/components/home/Icona";
import { ETICHETTA_ACCESA, FUOCO_HOME, RIMANDO } from "@/components/home/stile";
import { TONO_ERRORE } from "@/components/ui/MessaggioErrore";
import { DatoSezione, Introduzione, TestaSezione } from "@/components/servizi/monitoraggio/PezziPagina";
import { NUMERO_PASSO, PULSANTE } from "@/components/servizi/monitoraggio/stile";
import {
  CAMPO_ESCA,
  LIMITI,
  ORDINE_CAMPI,
  TIPI_ORGANIZZAZIONE,
  type ErroriContatto,
  type TipoOrganizzazione,
} from "@/lib/contatti";
import { CONSERVAZIONE, EMAIL_TITOLARE } from "@/lib/legale";
import { CONDIZIONI, RISPOSTA } from "@/lib/promesse";
import { AREE_PAGINA, TOTALE_SERVIZI } from "@/lib/serviziPagina";

/* Il modulo di /contatti, rifatto il 19/10/2026 sul modello
   `uploads/contatti.html` (istruzioni `uploads/ISTRUZIONI-contatti.md`):
   «Scrivici · 3 passaggi» (01 i servizi, 02 chi sei, 03 il messaggio),
   poi «Prima di inviare» con informativa, caselle, bottone e il pannello
   scuro coi quattro numeri. Dopo una risposta 200, e solo allora, il
   modulo lascia il posto alla conferma del modello.

   L'invio è quello di prima, che il modello lasciava da collegare (§ 4):
   `POST /api/contatti` in JSON, validazione per campo sul server, fuoco
   sul primo campo sbagliato, `msCompilazione`, il campo esca, il 503
   quando il canale non è collegato, il bottone che non cambia larghezza.

   Che cosa si controlla qui e che cosa sul server. Il modello controlla
   sul posto una cosa sola, la presa visione dell'informativa (§ 3:
   «compare l'avviso rosso e la pagina porta alla casella»), e così si fa
   qui. Tutto il resto lo giudica il server con `validaContatto`, che
   risponde 400 con la mappa campo → frase: la stessa che il modulo
   disegna, senza tradurla. Un secondo giudice nel browser direbbe le
   stesse frasi un decimo di secondo prima, ma sarebbe una copia da
   tenere allineata, e chi la aggira con la console arriva comunque al
   server. */

/** `compilazione` è anche lo stato in cui si torna dopo un errore: il
 *  modulo resta pieno di quello che era stato scritto, che è il minimo
 *  dovuto a chi ha appena composto quindici righe. */
type Stato = "compilazione" | "invio" | "inviato";

/** Le 14 tessere di «01 · I servizi», nell'ordine e con la numerazione
 *  del catalogo di /servizi (`AREE_PAGINA`, «Servizio 01 di 14»), che è
 *  anche quella del modello (S01–S14). Gli slug sono quelli che
 *  `validaContatto` accetta in `interessi` (lib/services.ts: gli stessi
 *  quattordici). Nome breve e icona sono quelli delle tessere del modello
 *  (`data-area`, `#i-…`), che non coincidono coi titoli lunghi del
 *  catalogo: una tessera da 150 pixel vuole «Strategia», non
 *  «Pianificazione strategica». */
const TESSERA: Record<string, { nome: string; icona: NomeIcona }> = {
  monitoraggio: { nome: "Monitoraggio", icona: "segnale" },
  report: { nome: "Report", icona: "report" },
  "verifica-informativa": { nome: "Verifica informativa", icona: "cerca" },
  analisi: { nome: "Analisi", icona: "scenari" },
  "presenza-internazionale": { nome: "Presenza internazionale", icona: "esteri" },
  "import-export": { nome: "Import / Export", icona: "commercio" },
  "materie-prime": { nome: "Materie prime", icona: "energia" },
  "due-diligence": { nome: "Due diligence", icona: "impresa" },
  "rischio-reputazionale": { nome: "Reputazione", icona: "focus" },
  sanzioni: { nome: "Sanzioni", icona: "sanzioni" },
  "cibernetica-aziendale": { nome: "Cibernetica", icona: "difesa" },
  informatica: { nome: "Informatica", icona: "tecnologia" },
  giuridico: { nome: "Giuridico", icona: "istituzione" },
  "pianificazione-strategica": { nome: "Strategia", icona: "caso" },
};

const TESSERE = AREE_PAGINA.flatMap((a) => a.servizi).map((s) => ({
  slug: s.slug,
  numero: s.numero,
  nome: TESSERA[s.slug]?.nome ?? s.titolo,
  icona: TESSERA[s.slug]?.icona ?? s.icona,
}));

/** L'icona di ogni profilo, come nei chip del modello (`data-profilo`).
 *  Il vocabolario dei profili è `TIPI_ORGANIZZAZIONE`, che il server
 *  legge: qui c'è solo la veste. */
const ICONA_PROFILO: Record<TipoOrganizzazione, NomeIcona> = {
  pmi: "pmi",
  multinazionale: "azienda",
  istituzione: "istituzione",
  professionista: "persona",
  ricercatore: "dossier",
  associazione: "notizia",
};

/** Il riquadro di un passaggio (`.m-p.ct-p`): bordo inchiostro, 40 di
 *  rientro in cima, la sfumatura dalla superficie all'acqua. */
const PASSO =
  "relative m-0 flex min-w-0 flex-col gap-3.5 overflow-hidden border border-ink px-[26px] pt-10 transition-opacity disabled:opacity-60 max-sm:px-[18px]";

/** La legenda di un passaggio. Flottante come `.ct-p legend` del
 *  modello: una `<legend>` normale il browser la disegna dentro il bordo
 *  del fieldset, sopra i 40 pixel di rientro; flottante torna un blocco
 *  in cima al contenuto, e chi la segue la chiude con `clear-both`. */
const LEGENDA = `float-left w-full p-0 ${NUMERO_PASSO}`;

/** La domanda di un passaggio (`.m-p .h`): Gelasio 26. */
const DOMANDA = "m-0 clear-both font-serif text-[26px] leading-[1.2] font-normal max-sm:text-[23px]";

/** Il testo d'aiuto (`.ct-aiuto`): Geist 14 sonda. */
const AIUTO = "m-0 font-sans text-corpo leading-[1.5] text-sommesso";

/** Il campo di testo (`.ct-campo input`): Geist 15, bordo del mare al 45%
 *  sul sonda (7,38:1 nel chiaro, 6,68 nello scuro), superficie. Il bordo
 *  passa da una variabile perché un errore lo deve poter cambiare senza
 *  litigare con la classe. */
const INPUT = `w-full rounded-[2px] border border-[var(--bordo-campo)] bg-surface px-3.5 py-3 font-sans text-sommario text-ink ${FUOCO_HOME}`;

/** Le due cifre del contatore e dei numeri di tessera: «00», «03». */
const dueCifre = (n: number) => String(n).padStart(2, "0");

export function ContactForm() {
  const params = useSearchParams();

  const [tipo, setTipo] = useState<TipoOrganizzazione | "">("");
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [azienda, setAzienda] = useState("");
  const [sito, setSito] = useState("");
  const [interessi, setInteressi] = useState<string[]>([]);
  const [messaggio, setMessaggio] = useState("");

  /** Presa visione dell'informativa: obbligatoria, e per questo parte
   *  spenta. Una casella già spuntata non è una dichiarazione di
   *  nessuno, è una riga che qualcun altro ha scritto al posto suo. */
  const [presaVisione, setPresaVisione] = useState(false);
  /** Consenso agli aggiornamenti: facoltativo, separato, e spento
   *  anch'esso. Sta accanto all'altro ma non è la stessa cosa, ed è il
   *  motivo per cui sono due caselle e non una. */
  const [consensoAggiornamenti, setConsensoAggiornamenti] = useState(false);

  const [stato, setStato] = useState<Stato>("compilazione");
  const [errori, setErrori] = useState<ErroriContatto>({});
  /** L'avviso sopra il bottone: il messaggio del server quando il guasto
   *  non riguarda un campo (rete assente, canale giù, troppe richieste),
   *  oppure il primo errore di campo, ripetuto vicino al bottone che si è
   *  appena premuto. */
  const [avviso, setAvviso] = useState<string | null>(null);

  /** Campo esca (`CAMPO_ESCA`): nella pagina non si vede, e se arriva
   *  compilato la richiesta viene scartata dal server. Sta nello stato
   *  come tutti gli altri campi per non uscire dal modo controllato di
   *  React. */
  const [esca, setEsca] = useState("");

  /** Istante del montaggio (e di ogni «Scrivi un altro messaggio»), per
   *  dire al server quanto è durata la compilazione. In un `useEffect` e
   *  non in un initializer di `useState`: `Date.now()` durante il render
   *  darebbe un valore diverso fra server e client. */
  const apertoIl = useRef<number | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const confermaRef = useRef<HTMLHeadingElement>(null);
  /** Dopo «Scrivi un altro messaggio» il fuoco torna in cima al modulo:
   *  il bottone che lo aveva su sparisce con la conferma. */
  const ripartito = useRef(false);

  useEffect(() => {
    apertoIl.current = Date.now();
  }, []);

  /** Il fuoco sulla conferma quando arriva, e sul modulo quando torna. In
   *  un effetto e non nel gestore perché i due blocchi non esistono
   *  ancora nel giro in cui lo stato cambia: React li monta dopo. */
  useEffect(() => {
    if (stato === "inviato") {
      confermaRef.current?.focus({ preventScroll: true });
      confermaRef.current?.closest("section")?.scrollIntoView({ block: "start", behavior: "smooth" });
    } else if (stato === "compilazione" && ripartito.current) {
      ripartito.current = false;
      const primo = formRef.current?.querySelector<HTMLElement>('[data-campo="interessi"]');
      primo?.focus({ preventScroll: true });
      formRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
    }
  }, [stato]);

  /** `?service=<slug>` accende la tessera di quel servizio: è il modo in
   *  cui le pagine dei servizi mandano qui («Definiamo il tuo
   *  monitoraggio»). Uno slug che non è una tessera si ignora. */
  useEffect(() => {
    const slug = params.get("service");
    if (slug && TESSERE.some((t) => t.slug === slug)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- il servizio arriva dall'indirizzo, che si legge solo dopo l'idratazione
      setInteressi((prev) => (prev.includes(slug) ? prev : [...prev, slug]));
    }
  }, [params]);

  function cambiaServizio(slug: string) {
    setInteressi((v) => (v.includes(slug) ? v.filter((x) => x !== slug) : [...v, slug]));
  }

  /** Porta il fuoco sul primo campo sbagliato, in ordine di lettura. Un
   *  modulo alto tre schermate può avere l'errore fuori dalla finestra:
   *  senza questo, chi clicca vede solo che non succede niente. */
  function portaAlPrimoErrore(e: ErroriContatto) {
    const campo = ORDINE_CAMPI.find((c) => e[c]);
    if (!campo || !formRef.current) return;
    const nodo = formRef.current.querySelector<HTMLElement>(`[data-campo="${campo}"]`);
    if (!nodo) return;
    nodo.scrollIntoView({ block: "center", behavior: "smooth" });
    nodo.focus({ preventScroll: true });
  }

  /** Gli errori a cui portare il fuoco al prossimo render. Non si può
   *  farlo subito: dopo una risposta del server i campi sono ancora dentro
   *  i fieldset spenti dall'invio, e un campo `disabled` non prende il
   *  fuoco (misurato: finiva sul `body`). L'effetto qui sotto gira quando
   *  React li ha già riaccesi. */
  const daSegnalare = useRef<ErroriContatto | null>(null);

  useEffect(() => {
    if (stato !== "compilazione" || !daSegnalare.current) return;
    portaAlPrimoErrore(daSegnalare.current);
    daSegnalare.current = null;
  });

  function segnala(e: ErroriContatto, messaggioAvviso: string | null) {
    setErrori(e);
    setAvviso(messaggioAvviso);
    daSegnalare.current = e;
  }

  async function invia(ev: React.FormEvent) {
    ev.preventDefault();
    if (stato === "invio") return;

    // L'unico controllo sul posto, come nel modello: senza la presa
    // visione la richiesta non parte nemmeno, l'avviso compare sotto le
    // caselle e il fuoco va sulla casella. La frase è quella di
    // `validaContatto`, che il server rimanderebbe uguale.
    if (!presaVisione) {
      segnala(
        { presaVisione: "Per inviare la richiesta conferma di aver letto l'informativa privacy." },
        null
      );
      return;
    }

    setStato("invio");
    setErrori({});
    setAvviso(null);

    try {
      const resp = await fetch("/api/contatti", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          modulo: "contatti",
          tipo: tipo || null,
          nome,
          email,
          azienda,
          sito,
          interessi,
          messaggio,
          presaVisione,
          consensoAggiornamenti,
          [CAMPO_ESCA]: esca,
          msCompilazione: apertoIl.current === null ? null : Date.now() - apertoIl.current,
        }),
      });

      const corpo: { ok?: boolean; messaggio?: string; errori?: ErroriContatto } | null =
        await resp.json().catch(() => null);

      // La conferma solo con un 200 e un `ok` dentro: un proxy che
      // risponde 200 con una pagina d'errore in HTML non è una richiesta
      // arrivata, e ringraziare per un messaggio che nessuno ha ricevuto
      // è l'unico esito inaccettabile.
      if (resp.status !== 200 || !corpo?.ok) {
        setStato("compilazione");
        segnala(
          corpo?.errori ?? {},
          corpo?.messaggio ??
            `Non siamo riusciti a inviare il messaggio. Riprova fra poco, oppure scrivi a ${EMAIL_TITOLARE}.`
        );
        return;
      }

      setStato("inviato");
    } catch {
      // Qui si finisce quando la richiesta non è nemmeno partita: rete
      // assente, pagina chiusa a metà, filtro aziendale. Il messaggio
      // deve dire che non è partita, perché è l'unica informazione che
      // cambia il comportamento di chi legge.
      setStato("compilazione");
      setAvviso(
        `Connessione assente o interrotta: il messaggio non è partito. Riprova fra poco, oppure scrivi a ${EMAIL_TITOLARE}.`
      );
    }
  }

  /** «Scrivi un altro messaggio»: svuota tutto, compresi servizi, profilo
   *  e caselle, e riparte il cronometro della compilazione. Il servizio
   *  dell'indirizzo non torna: chi ricomincia sceglie da capo. */
  function ricomincia() {
    setTipo("");
    setNome("");
    setEmail("");
    setAzienda("");
    setSito("");
    setInteressi([]);
    setMessaggio("");
    setPresaVisione(false);
    setConsensoAggiornamenti(false);
    setErrori({});
    setAvviso(null);
    setEsca("");
    apertoIl.current = Date.now();
    ripartito.current = true;
    setStato("compilazione");
  }

  if (stato === "inviato") {
    return <Conferma titoloRef={confermaRef} onRicomincia={ricomincia} />;
  }

  const inviando = stato === "invio";
  const scelti = TESSERE.filter((t) => interessi.includes(t.slug));

  return (
    <form
      ref={formRef}
      onSubmit={invia}
      // `noValidate` toglie di mezzo i fumetti del browser, che parlano
      // la lingua del browser e non quella del sito, e lascia parlare i
      // messaggi di `lib/contatti.ts`, che sono quelli che il server
      // rimanda indietro. Gli attributi `required` restano sui campi:
      // servono ai lettori di schermo, non a bloccare l'invio.
      noValidate
      aria-busy={inviando}
      className="scroll-mt-4"
    >
      <section aria-labelledby="ct-scrivi" className="mt-[60px]">
        <TestaSezione id="ct-scrivi" titolo="Scrivici" destra={<DatoSezione>3 passaggi</DatoSezione>} />
        <Introduzione
          sinistra={
            <>
              Scegli i servizi, raccontaci la tua esigenza.
              <br />
              <span className="text-mare">Ti rispondiamo entro {RISPOSTA.ore}.</span>
            </>
          }
          destra="Ogni richiesta viene letta e presa in carico da chi segue il tema, sotto accordo di riservatezza."
        />

        {/* 01 · I servizi */}
        <fieldset
          disabled={inviando}
          className={`${PASSO} mt-6 bg-[image:var(--oi-ct-servizi)] pb-7`}
        >
          <legend className={LEGENDA}>01 · I servizi · facoltativo</legend>
          <div className="clear-both flex flex-wrap items-center justify-between gap-x-8 gap-y-2">
            <p className={DOMANDA}>Su quali servizi vuoi confrontarti?</p>
            <p
              aria-live="polite"
              className="m-0 font-mono text-meta tracking-[0.08em] text-sommesso uppercase"
            >
              <b className="mr-1 text-[22px] font-semibold text-segnale">{dueCifre(interessi.length)}</b> /{" "}
              {TOTALE_SERVIZI} selezionati
            </p>
            <p className={`${AIUTO} basis-full`}>
              {`Seleziona uno o più dei ${TOTALE_SERVIZI} servizi dell’Osservatorio. Se non sai quale, lascia vuoto: lo capiamo insieme.`}
            </p>
          </div>
          <div className="mt-1.5 grid grid-cols-2 gap-2.5 min-[600px]:grid-cols-4 min-[1100px]:grid-cols-7">
            {TESSERE.map((t, i) => (
              <Tessera
                key={t.slug}
                numero={t.numero}
                nome={t.nome}
                icona={t.icona}
                acceso={interessi.includes(t.slug)}
                ancora={i === 0}
                onClick={() => cambiaServizio(t.slug)}
              />
            ))}
          </div>
          {errori.interessi ? <Errore campo="interessi">{errori.interessi}</Errore> : null}
        </fieldset>

        <div className="mt-6 grid grid-cols-1 gap-6 min-[900px]:grid-cols-2">
          {/* 02 · Chi sei */}
          <fieldset disabled={inviando} className={`${PASSO} bg-[image:var(--oi-ct-passo)]`}>
            <legend className={LEGENDA}>02 · Chi sei</legend>
            <p className={DOMANDA}>Parlaci della tua esigenza.</p>
            <p className={AIUTO}>Puoi indicare il tuo profilo, se pertinente.</p>
            {/* Un profilo solo, o nessuno (`tipo` in lib/contatti.ts):
                ricliccare quello acceso lo spegne. */}
            <div role="group" aria-label="Profilo" className="flex flex-wrap gap-2">
              {TIPI_ORGANIZZAZIONE.map((o, i) => {
                const acceso = tipo === o.id;
                return (
                  <button
                    key={o.id}
                    type="button"
                    // L'ancora del fuoco sta sul primo chip: il campo
                    // «tipo» non è un input solo, è sei bottoni.
                    data-campo={i === 0 ? "tipo" : undefined}
                    aria-pressed={acceso}
                    onClick={() => setTipo(acceso ? "" : o.id)}
                    className={`group/chip inline-flex min-h-10 cursor-pointer items-center gap-2 rounded-[2px] border border-rule bg-surface px-3.5 py-2 font-sans text-corpo font-medium text-ink transition-colors hover:border-ink aria-pressed:border-mare aria-pressed:bg-mare aria-pressed:text-suMare ${FUOCO_HOME}`}
                  >
                    <Icona
                      nome={ICONA_PROFILO[o.id]}
                      className="size-4 text-mare group-aria-pressed/chip:text-suMare"
                    />
                    {o.label}
                  </button>
                );
              })}
            </div>
            {errori.tipo ? <Errore campo="tipo">{errori.tipo}</Errore> : null}

            <div className="grid grid-cols-1 gap-x-4 gap-y-3.5 min-[900px]:grid-cols-2">
              <Campo
                campo="nome"
                etichetta="Nome e cognome"
                obbligatorio
                errore={errori.nome}
                valore={nome}
                onCambia={setNome}
                autoComplete="name"
                maxLength={LIMITI.nome.max}
              />
              <Campo
                campo="email"
                etichetta="Email"
                obbligatorio
                errore={errori.email}
                valore={email}
                onCambia={setEmail}
                type="email"
                inputMode="email"
                autoComplete="email"
                maxLength={LIMITI.email.max}
              />
              <Campo
                campo="azienda"
                etichetta="Azienda o ente"
                errore={errori.azienda}
                valore={azienda}
                onCambia={setAzienda}
                autoComplete="organization"
                maxLength={LIMITI.azienda.max}
              />
              <Campo
                campo="sito"
                etichetta="Sito web"
                errore={errori.sito}
                valore={sito}
                onCambia={setSito}
                type="url"
                inputMode="url"
                autoComplete="url"
                placeholder="https://"
                maxLength={LIMITI.sito.max}
              />
            </div>

            {/* Campo esca. Non `display:none` e non `type="hidden"`: i bot
                più svegli saltano i campi nascosti in CSS e quelli hidden
                per definizione. Qui è un input vero, fuori dalla finestra
                (dentro un fieldset `overflow-hidden`, quindi non apre
                scorrimento di lato), escluso dal giro del TAB e dai
                lettori di schermo, e `autoComplete="off"` tiene lontano il
                riempimento automatico del browser, che è l'unico modo in
                cui una persona vera potrebbe compilarlo per sbaglio. */}
            <div aria-hidden className="pointer-events-none absolute left-[-9999px] top-0">
              <label>
                Fax
                <input
                  type="text"
                  name={CAMPO_ESCA}
                  tabIndex={-1}
                  autoComplete="off"
                  value={esca}
                  onChange={(e) => setEsca(e.target.value)}
                />
              </label>
            </div>

            <Striscia>
              <MiniProfili />
            </Striscia>
          </fieldset>

          {/* 03 · Il tuo messaggio */}
          <fieldset disabled={inviando} className={`${PASSO} bg-[image:var(--oi-ct-passo)]`}>
            <legend className={LEGENDA}>03 · Il tuo messaggio</legend>
            <p className={DOMANDA}>Come possiamo esserti utili?</p>
            {scelti.length ? (
              <p className="m-0 flex flex-wrap items-baseline gap-x-2.5 gap-y-1 border-l-[3px] border-segnale bg-surface px-3 py-2.5 font-sans text-corpo font-semibold text-ink">
                <span className="font-mono text-[11px] font-normal tracking-[0.08em] text-segnale uppercase">
                  Servizi scelti
                </span>
                {scelti.map((t) => t.nome).join(" · ")}
              </p>
            ) : null}
            <Campo
              campo="messaggio"
              etichetta="Il tuo messaggio"
              obbligatorio
              errore={errori.messaggio}
              valore={messaggio}
              onCambia={setMessaggio}
              multiriga
              maxLength={LIMITI.messaggio.max}
              placeholder="Descrivi la richiesta e aggiungi il contesto che ritieni utile."
            />
            <Striscia>
              <MiniMessaggio />
            </Striscia>
          </fieldset>
        </div>
      </section>

      {/* Prima di inviare */}
      <section
        aria-labelledby="ct-cta-h"
        className="mt-[60px] grid grid-cols-1 overflow-hidden border border-t-4 border-ink border-t-segnale bg-[image:var(--oi-ct-invio)] min-[1000px]:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]"
      >
        <div className="flex flex-col gap-3 px-11 py-9 max-md:px-[22px] max-md:py-7">
          <InformativaConsensi
            variante="pannello"
            dati="nome, indirizzo email, azienda, sito web, profilo, servizi di interesse e testo del messaggio"
            testa={
              <h2 id="ct-cta-h" className="m-0 font-serif text-[34px] leading-[1.15] font-normal max-sm:text-[28px]">
                Prima di inviare.
              </h2>
            }
            presaVisione={presaVisione}
            onPresaVisione={(v) => {
              setPresaVisione(v);
              // Come nel modello, l'avviso se ne va appena la casella
              // viene spuntata: resta sbagliato solo quello che lo è.
              if (v && errori.presaVisione) {
                setErrori((prima) => {
                  const dopo = { ...prima };
                  delete dopo.presaVisione;
                  return dopo;
                });
              }
            }}
            consensoAggiornamenti={consensoAggiornamenti}
            onConsensoAggiornamenti={setConsensoAggiornamenti}
            errorePresaVisione={errori.presaVisione}
            disabled={inviando}
          />

          {avviso ? (
            <p
              role="alert"
              className="m-0 border-l-[3px] bg-bg px-3.5 py-2.5 font-sans text-corpo text-ink"
              style={{ borderColor: TONO_ERRORE }}
            >
              <b className="font-semibold">Messaggio non inviato.</b> {avviso}
            </p>
          ) : null}

          <div className="mt-1.5 flex flex-wrap items-center gap-x-6 gap-y-3">
            <button
              type="submit"
              disabled={inviando}
              className={`${PULSANTE} min-h-11 cursor-pointer border-0 disabled:cursor-progress disabled:opacity-70`}
            >
              {/* Le due etichette stanno nella stessa cella di una
                  griglia, una sopra l'altra: il bottone è largo quanto la
                  più lunga e non cambia misura quando passa a «Invio in
                  corso», che sposterebbe di lato il rimando accanto. È il
                  gemello invisibile di `Ticker` e `AuthMenu`. */}
              <span className="inline-grid">
                <span
                  className="col-start-1 row-start-1"
                  style={{ visibility: inviando ? "hidden" : undefined }}
                >
                  Invia il messaggio ↗
                </span>
                <span
                  className="col-start-1 row-start-1"
                  style={{ visibility: inviando ? undefined : "hidden" }}
                  aria-hidden={!inviando}
                >
                  Invio in corso…
                </span>
              </span>
            </button>
            <Link href="/nda" className={RIMANDO}>
              Accordo di riservatezza ↗
            </Link>
          </div>
          <p className="m-0 mt-2.5 font-sans text-voce text-sommesso">
            <span aria-hidden>* </span>Campi obbligatori
          </p>
        </div>

        <DatiScuri
          dati={[
            valoreDi(RISPOSTA.breve),
            valoreDi("NDA"),
            { v: "Riservato", k: "Nessuna cessione a terzi" },
            // «24 mesi», da lib/legale.ts: lo stesso numero dell'informativa.
            { v: CONSERVAZIONE.breve, k: "Poi i dati si cancellano" },
          ]}
        />
      </section>
    </form>
  );
}

/** Le condizioni del pannello scuro che `lib/promesse.ts` ha già
 *  («48h · Prima risposta», «NDA · Sotto accordo»): si prendono da lì,
 *  così cambiano insieme al resto del sito. */
function valoreDi(v: string): { v: string; k: string } {
  return CONDIZIONI.find((c) => c.v === v) ?? { v, k: "" };
}

/** Il pannello scuro accanto all'invio e alla conferma (`.ct-page .m-cta
 *  .m-dati`): quattro numeri Gelasio 30 in carta su `fasciaMare`, la
 *  didascalia mono rame chiaro, filetti di carta al 18% fra le celle. */
function DatiScuri({ dati }: { dati: { v: string; k: string }[] }) {
  return (
    <dl className="m-0 grid grid-cols-2 content-center bg-fasciaMare px-10 py-9 max-md:px-[22px] max-md:py-7">
      {dati.map((d, i) => (
        <div
          key={d.v}
          className={`border-[var(--oi-ct-filetto-fascia)] px-5 py-[18px] max-sm:px-3 ${
            i % 2 === 0 ? "border-r" : ""
          } ${i < dati.length - 2 ? "border-b" : ""}`}
        >
          <dt className="font-serif text-[30px] leading-[1.1] text-[var(--oi-os-carta)] max-sm:text-[24px]">{d.v}</dt>
          <dd className="m-0 mt-1.5 font-mono text-meta tracking-[0.06em] text-[var(--oi-ct-rame-su-fascia)] uppercase">
            {d.k}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** La conferma (`#ct-blocco-ok` del modello): compare al posto del modulo
 *  solo dopo un 200. Il titolo prende il fuoco, e la sezione è
 *  `role="status"`: chi legge con un lettore di schermo sente che il
 *  messaggio è partito. */
function Conferma({
  titoloRef,
  onRicomincia,
}: {
  titoloRef: React.Ref<HTMLHeadingElement>;
  onRicomincia: () => void;
}) {
  return (
    <section
      role="status"
      aria-labelledby="ct-ok-h"
      className="mt-10 grid scroll-mt-4 grid-cols-1 overflow-hidden border border-t-4 border-ink border-t-segnale bg-[image:var(--oi-ct-invio)] min-[1000px]:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]"
    >
      <div className="flex flex-col gap-3 px-11 py-9 max-md:px-[22px] max-md:py-7">
        <p className={`m-0 ${ETICHETTA_ACCESA}`}>Messaggio inviato · Modalità riservata</p>
        <h2
          id="ct-ok-h"
          ref={titoloRef}
          tabIndex={-1}
          className="m-0 font-serif text-[34px] leading-[1.15] font-normal outline-none max-sm:text-[28px]"
        >
          Grazie, abbiamo ricevuto la tua richiesta.
        </h2>
        <p className="m-0 font-sans text-sommario leading-[1.55] text-sommesso">
          Ti risponde una persona dell&apos;Osservatorio entro {RISPOSTA.ore} lavorative. La
          conversazione resta riservata, sotto NDA.
        </p>
        <div className="mt-1.5">
          <button type="button" onClick={onRicomincia} className={`${PULSANTE} min-h-11 cursor-pointer border-0`}>
            Scrivi un altro messaggio
          </button>
        </div>
      </div>
      <DatiScuri
        dati={[
          valoreDi(RISPOSTA.breve),
          valoreDi("NDA"),
          { v: "1", k: "Persona che ti segue" },
          { v: "0", k: "Dati ceduti a terzi" },
        ]}
      />
    </section>
  );
}

/** Una tessera di «01 · I servizi» (`.ct-te`): bottone vero con
 *  `aria-pressed`, riquadro icona (`.ct-bd`) che si inclina al
 *  passaggio, numero «S01» mono, nome Geist 14/600, quadretto di spunta
 *  in alto a destra. Scelta: fondo del mare al 14%, bordo mare, riga in
 *  cima rame. */
function Tessera({
  numero,
  nome,
  icona,
  acceso,
  ancora,
  onClick,
}: {
  numero: string;
  nome: string;
  icona: NomeIcona;
  acceso: boolean;
  ancora: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={acceso}
      data-campo={ancora ? "interessi" : undefined}
      onClick={onClick}
      className={`group/tessera relative flex min-h-28 cursor-pointer flex-col items-start gap-1.5 rounded-[2px] border border-t-[3px] border-rule bg-surface px-3.5 py-3 text-left text-ink transition-colors hover:border-ink hover:border-t-segnale aria-pressed:border-mare aria-pressed:border-t-segnale aria-pressed:bg-[var(--oi-ct-scelta)] ${FUOCO_HOME}`}
    >
      <span
        aria-hidden
        className="grid size-[30px] place-items-center rounded-[2px] border border-rule bg-surface transition-transform duration-[250ms] group-hover/tessera:scale-[1.08] group-hover/tessera:-rotate-6 group-hover/tessera:border-mare group-aria-pressed/tessera:border-mare group-aria-pressed/tessera:bg-mare motion-reduce:transition-none"
      >
        <Icona nome={icona} className="size-[17px] text-mare group-aria-pressed/tessera:text-suMare" />
      </span>
      <span className="font-mono text-[11px] tracking-[0.08em] text-sommesso group-aria-pressed/tessera:text-segnale">
        S{numero}
      </span>
      <span className="font-sans text-corpo leading-[1.3] font-semibold">{nome}</span>
      <span
        aria-hidden
        className="absolute top-2.5 right-2.5 size-3.5 rounded-[2px] border border-sommesso group-aria-pressed/tessera:border-mare group-aria-pressed/tessera:bg-mare group-aria-pressed/tessera:shadow-[inset_0_0_0_3px_var(--oi-ct-scelta)]"
      />
    </button>
  );
}

/** Un campo del modulo (`.ct-campo`): etichetta Geist 14/600, asterisco
 *  rame per gli obbligatori, «facoltativo» Geist 13 sonda per gli altri,
 *  l'errore sotto nel tono degli errori del sito, legato al campo con
 *  `aria-describedby`. */
function Campo({
  campo,
  etichetta,
  obbligatorio = false,
  errore,
  valore,
  onCambia,
  multiriga = false,
  type = "text",
  inputMode,
  autoComplete,
  placeholder,
  maxLength,
}: {
  campo: "nome" | "email" | "azienda" | "sito" | "messaggio";
  etichetta: string;
  obbligatorio?: boolean;
  errore?: string;
  valore: string;
  onCambia: (v: string) => void;
  multiriga?: boolean;
  type?: "text" | "email" | "url";
  inputMode?: "email" | "url";
  autoComplete?: string;
  placeholder?: string;
  maxLength: number;
}) {
  const id = `ct-${campo}`;
  const idErrore = `errore-${campo}`;
  const comuni = {
    id,
    name: campo,
    "data-campo": campo,
    value: valore,
    required: obbligatorio,
    maxLength,
    placeholder,
    "aria-invalid": errore ? true : undefined,
    "aria-describedby": errore ? idErrore : undefined,
    style: {
      "--bordo-campo": errore
        ? TONO_ERRORE
        : "color-mix(in srgb, var(--color-mare) 45%, var(--color-sommesso))",
    } as React.CSSProperties,
  };
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="font-sans text-corpo font-semibold text-ink">
        {etichetta}{" "}
        {obbligatorio ? (
          <b aria-hidden className="text-segnale">
            *
          </b>
        ) : (
          <i className="font-sans text-voce font-normal not-italic text-sommesso">facoltativo</i>
        )}
      </label>
      {multiriga ? (
        <textarea
          {...comuni}
          rows={6}
          onChange={(e) => onCambia(e.target.value)}
          className={`${INPUT} min-h-[236px] resize-y leading-[1.55]`}
        />
      ) : (
        <input
          {...comuni}
          type={type}
          inputMode={inputMode}
          autoComplete={autoComplete}
          onChange={(e) => onCambia(e.target.value)}
          className={INPUT}
        />
      )}
      {errore ? <Errore campo={campo}>{errore}</Errore> : null}
    </div>
  );
}

/** La riga d'errore di un campo: Geist 13, nel rosso della scala
 *  editoriale (`TONO_ERRORE`, dichiarato nei due temi). */
function Errore({ campo, children }: { campo: string; children: ReactNode }) {
  return (
    <p id={`errore-${campo}`} className="m-0 font-sans text-voce leading-[1.45]" style={{ color: TONO_ERRORE }}>
      {children}
    </p>
  );
}

/** La striscia disegnata in fondo a un passaggio (`.m-p .m-mini`): larga
 *  quanto il riquadro, bordi compresi del rientro, sul fondo dei disegni;
 *  `mt-auto` la spinge in fondo, così le due strisce dei passaggi 02 e 03
 *  stanno sulla stessa riga anche quando i riquadri hanno testi diversi. */
function Striscia({ children }: { children: ReactNode }) {
  return (
    <div className="-mx-[26px] mt-auto bg-[var(--oi-sv-fondo)] max-sm:-mx-[18px]">{children}</div>
  );
}
